import React, { useState, useRef, useEffect } from 'react';
import { 
  Bold, 
  Italic, 
  Underline, 
  List, 
  ListOrdered, 
  Link, 
  Check, 
  X,
  Type
} from 'lucide-react';

interface RichTextEditorProps {
  initialValue: string;
  onSave: (value: string) => void;
  onCancel: () => void;
  isOpen: boolean;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  initialValue,
  onSave,
  onCancel,
  isOpen,
}) => {
  const [content, setContent] = useState(initialValue);
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && editorRef.current) {
      // Siempre establecer el contenido cuando se abre el modal
      editorRef.current.innerHTML = initialValue;
      setContent(initialValue);
      editorRef.current.focus();
    }
  }, [isOpen, initialValue]);

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const handleSave = () => {
    const htmlContent = editorRef.current?.innerHTML || '';
    onSave(htmlContent);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'b':
          e.preventDefault();
          execCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          execCommand('italic');
          break;
        case 'u':
          e.preventDefault();
          execCommand('underline');
          break;
        case 's':
          e.preventDefault();
          handleSave();
          break;
        case 'Escape':
          e.preventDefault();
          onCancel();
          break;
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="rich-text-modal-overlay">
      <div className="rich-text-modal">
        <div className="rich-text-header">
          <div className="rich-text-title">
            <Type size={20} />
            <h3>Editor de Texto Enriquecido</h3>
          </div>
          <div className="rich-text-actions">
            <button
              className="btn btn-success"
              onClick={handleSave}
              title="Guardar (Ctrl+S)"
            >
              <Check size={16} />
              Guardar
            </button>
            <button
              className="btn btn-secondary"
              onClick={onCancel}
              title="Cancelar (Esc)"
            >
              <X size={16} />
              Cancelar
            </button>
          </div>
        </div>

        <div className="rich-text-toolbar">
          <div className="toolbar-group">
            <button
              className="toolbar-btn"
              onClick={() => execCommand('bold')}
              title="Negrita (Ctrl+B)"
            >
              <Bold size={16} />
            </button>
            <button
              className="toolbar-btn"
              onClick={() => execCommand('italic')}
              title="Cursiva (Ctrl+I)"
            >
              <Italic size={16} />
            </button>
            <button
              className="toolbar-btn"
              onClick={() => execCommand('underline')}
              title="Subrayado (Ctrl+U)"
            >
              <Underline size={16} />
            </button>
          </div>

          <div className="toolbar-separator" />

          <div className="toolbar-group">
            <button
              className="toolbar-btn"
              onClick={() => execCommand('insertUnorderedList')}
              title="Lista con viñetas"
            >
              <List size={16} />
            </button>
            <button
              className="toolbar-btn"
              onClick={() => execCommand('insertOrderedList')}
              title="Lista numerada"
            >
              <ListOrdered size={16} />
            </button>
          </div>

          <div className="toolbar-separator" />

          <div className="toolbar-group">
            <button
              className="toolbar-btn"
              onClick={() => {
                const url = prompt('Ingresa la URL del enlace:');
                if (url) execCommand('createLink', url);
              }}
              title="Insertar enlace"
            >
              <Link size={16} />
            </button>
          </div>
        </div>

        <div className="rich-text-content">
        <div
          ref={editorRef}
          className="rich-text-editor"
          contentEditable
          onKeyDown={handleKeyDown}
          onInput={(e) => {
            const target = e.target as HTMLDivElement;
            setContent(target.innerHTML);
          }}
          style={{
            minHeight: '300px',
            padding: '16px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            outline: 'none',
            backgroundColor: 'white',
            fontSize: '14px',
            lineHeight: '1.5',
            fontFamily: 'inherit',
          }}
        />
        </div>

        <div className="rich-text-footer">
          <div className="rich-text-help">
            <small>
              <strong>Atajos:</strong> Ctrl+B (Negrita), Ctrl+I (Cursiva), Ctrl+U (Subrayado), 
              Ctrl+S (Guardar), Esc (Cancelar)
            </small>
          </div>
          <div className="rich-text-preview">
            <div className="preview-content">
              <strong>Vista previa:</strong>
              <div className="preview-text">
                {content.replace(/<[^>]*>/g, '').substring(0, 200)}
                {content.replace(/<[^>]*>/g, '').length > 200 ? '...' : ''}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
